import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { PrimaryCareTableRowData } from "../../../data-model/primaryCareTableRowData";
import { TableCellButtonWithThreeDots } from "../../../data-model/tableCells";
import { OrderStatus } from "../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../enums/coloredText";
import {
  PrimaryGray,
  PrimaryGreen,
  SecondaryYellow,
} from "../../../enums/colors";
import { PocAssignee, PocAssigneeLabels } from "../../../enums/pocAssignee";
import {
  SecondStpStatus,
  SecondStpStatusLabels,
} from "../../../enums/secondStpStatus";
import { capitalizeString } from "../../../utils/stringModifications";
import { getButtonConfig } from "../../cmmOrder/utils/getButtonLabel";
import {
  PrimaryCareTableColumnKeys,
  PrimaryCareTableHeaderLabels,
} from "../enums/primaryCareTableDetails";
import { buildAuthTypeBadgeCell } from "./utils/authTypeBadge";
import { buildKeySourceBadgeCell } from "./utils/keySourceBadge";

export const PharmaPrimaryCareTableHeader: TableHeader[] = [
  {
    label: PrimaryCareTableHeaderLabels.DATE_OF_SERVICE,
    key: PrimaryCareTableColumnKeys.DATE_OF_SERVICE,
    order: 1,
    width: 7,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: PrimaryCareTableHeaderLabels.TASK,
    key: PrimaryCareTableColumnKeys.TASK,
    order: 2,
    width: 7,
    type: TableCellType.BUTTON_LINK,
  },
  {
    label: PrimaryCareTableHeaderLabels.PATIENT_DETAILS,
    key: PrimaryCareTableColumnKeys.PATIENT_DETAILS,
    order: 3,
    width: 12,
    type: TableCellType.MULTILINE,
  },
  {
    label: PrimaryCareTableHeaderLabels.DATE_OF_BIRTH,
    key: PrimaryCareTableColumnKeys.DATE_OF_BIRTH,
    order: 4,
    width: 8,
    type: TableCellType.STRING,
  },
  {
    label: PrimaryCareTableHeaderLabels.AUTH_TYPE,
    key: PrimaryCareTableColumnKeys.AUTH_TYPE,
    order: 5,
    width: 8,
    type: TableCellType.BADGE,
  },
  {
    label: PrimaryCareTableHeaderLabels.KEY,
    key: PrimaryCareTableColumnKeys.KEY,
    order: 6,
    width: 9,
    type: TableCellType.ACTIONABLE_INPUT,
  },
  {
    label: PrimaryCareTableHeaderLabels.KEY_SOURCE,
    key: PrimaryCareTableColumnKeys.KEY_SOURCE,
    order: 7,
    width: 8,
    type: TableCellType.BADGE,
  },
  {
    label: PrimaryCareTableHeaderLabels.STATUS,
    key: PrimaryCareTableColumnKeys.STATUS,
    order: 8,
    width: 9,
    type: TableCellType.BADGE,
  },

  {
    label: PrimaryCareTableHeaderLabels.ACTIONS,
    key: PrimaryCareTableColumnKeys.ACTIONS,
    order: 9,
    width: 10,
    sortable: false,
    type: TableCellType.BUTTON_WITH_THREE_DOTS,
  },

  {
    label: PrimaryCareTableHeaderLabels.RESPONSE_STATUS,
    key: PrimaryCareTableColumnKeys.RESPONSE_STATUS,
    order: 10,
    width: 8,
    type: TableCellType.BADGE,
  },

  {
    label: PrimaryCareTableHeaderLabels.ROUTE,
    key: PrimaryCareTableColumnKeys.ROUTE,
    order: 11,
    width: 7,
    type: TableCellType.BADGE,
  },
  {
    label: PrimaryCareTableHeaderLabels.ASSIGNED_TO,
    key: PrimaryCareTableColumnKeys.ASSIGNED_TO,
    order: 12,
    width: 7,
    type: TableCellType.BADGE,
  },
  {
    label: PrimaryCareTableHeaderLabels.SUBMIT,
    key: PrimaryCareTableColumnKeys.SUBMIT,
    order: 13,
    width: 5,
    sortable: false,
    type: TableCellType.ADD_KEY_BUTTON,
  },
  {
    label: PrimaryCareTableHeaderLabels.UPDATE,
    key: PrimaryCareTableColumnKeys.UPDATE,
    order: 14,
    width: 8,
    type: TableCellType.TAG_WITH_DROPDOWN_MENU,
  },
];

export const generateTableDataForPrimaryCareTable = (
  data: AestraPrimaryCareTable[],
): PrimaryCareTableRowData[] => {
  return data.map((item) => {
    return {
      identifier: item.identifier,
      patient_mrn: item.patient_mrn ?? "",
      task_history: item.task_history ?? "",
      [PrimaryCareTableColumnKeys.KEY]: {
        data: item.covermymed_id ?? "",
        id: item.identifier,
        fieldId: PrimaryCareTableColumnKeys.KEY,
      },
      [PrimaryCareTableColumnKeys.KEY_SOURCE]: buildKeySourceBadgeCell(
        item.key_source,
      ),
      [PrimaryCareTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(item.patient_name ?? undefined),
        secondaryText: `MRN : ${item.patient_mrn ?? ""}`,
      },
      [PrimaryCareTableColumnKeys.DATE_OF_SERVICE]: item.dumped_at
        ? moment(item.dumped_at).format("MM/DD/YYYY")
        : "--",
      [PrimaryCareTableColumnKeys.DATE_OF_BIRTH]: item.dob ?? "--",
      [PrimaryCareTableColumnKeys.TASK]: item.seq ?? "--",
      [PrimaryCareTableColumnKeys.AUTH_TYPE]: buildAuthTypeBadgeCell(
        item.specialty,
      ),
      [PrimaryCareTableColumnKeys.STATUS]: item.cmm_status
        ? {
            text:
              SecondStpStatusLabels[item.cmm_status as SecondStpStatus] ??
              item.cmm_status,
            color:
              BadgeTextColor[
                (item.cmm_status as OrderStatus) ?? OrderStatus.NO_STATUS
              ] ?? BadgeTextColor[OrderStatus.NO_STATUS],
            bgColor:
              BadgeColor[
                (item.cmm_status as OrderStatus) ?? OrderStatus.NO_STATUS
              ] ?? BadgeColor[OrderStatus.NO_STATUS],
          }
        : {
            text: "Not Sent",
            color: BadgeTextColor[OrderStatus.NO_STATUS],
            bgColor: BadgeColor[OrderStatus.NO_STATUS],
          },
      [PrimaryCareTableColumnKeys.ACTIONS]: (() => {
        const buttonConfig = getButtonConfig(item.cmm_status ?? undefined);
        const button: TableCellButtonWithThreeDots = {
          label: buttonConfig.label,
          buttonId: buttonConfig.buttonId,
          disabled: buttonConfig.disabled ?? false,
          rowId: item.identifier,
          threeDotsOptions: [
            {
              id: "rerun_from_onco_emr",
              text: "Rerun from Onco Emr",
            },
            {
              id: "rerun_from_cmm",
              text: "Rerun from CMM",
            },
            {
              id: "report_prescription_inaccuracy",
              text: "Report Prescription Inaccuracy",
            },
            {
              id: "report_medication_inaccuracy",
              text: "Report Medication Inaccuracy",
            },
            {
              id: "report_inaccuracy",
              text: "Report Form Inaccuracy",
            },
          ],
        };
        return button;
      })(),
      [PrimaryCareTableColumnKeys.RESPONSE_STATUS]: {
        text: item.response_status || "No Status",
        color:
          BadgeTextColor[
            (item.response_status as OrderStatus) ?? OrderStatus.NO_STATUS
          ] ?? BadgeTextColor[OrderStatus.NO_STATUS],
        bgColor:
          BadgeColor[
            (item.response_status as OrderStatus) ?? OrderStatus.NO_STATUS
          ] ?? BadgeColor[OrderStatus.NO_STATUS],
      },
      [PrimaryCareTableColumnKeys.SUBMIT]: {
        value: item.cmm_result_key ? "Edit" : "Add",
        isDisabled: false,
        title: item.cmm_result_key ? "Edit" : "Add",
      },
      [PrimaryCareTableColumnKeys.ROUTE]: {
        text: item.route_via || "--",
        color:
          BadgeTextColor[
            (item.route_via as OrderStatus) ?? OrderStatus.NO_STATUS
          ] ?? BadgeTextColor[OrderStatus.NO_STATUS],
        bgColor:
          BadgeColor[
            (item.route_via as OrderStatus) ?? OrderStatus.NO_STATUS
          ] ?? BadgeColor[OrderStatus.NO_STATUS],
      },
      [PrimaryCareTableColumnKeys.ASSIGNED_TO]: {
        text: PocAssigneeLabels[item.poc as PocAssignee] || item.poc || "--",
        color:
          BadgeTextColor[
            (item.status as OrderStatus) ?? OrderStatus.NO_STATUS
          ] ?? BadgeTextColor[OrderStatus.NO_STATUS],
        bgColor:
          BadgeColor[(item.status as OrderStatus) ?? OrderStatus.NO_STATUS] ??
          BadgeColor[OrderStatus.NO_STATUS],
      },
      [PrimaryCareTableColumnKeys.UPDATE]: {
        label: "Update",
        backgroundColor: PrimaryGray.GRAY_16,
        textColor: PrimaryGray.GRAY_1,
        options: [
          {
            label: "In Progress",
            value: "in_progress",
            backgroundColor: SecondaryYellow.YELLOW_11,
            textColor: SecondaryYellow.YELLOW_1,
          },
          {
            label: "Completed",
            value: "completed",
            backgroundColor: PrimaryGreen.GREEN_11,
            textColor: PrimaryGreen.GREEN_1,
          },
        ],
        dataId: item.identifier,
        fieldId: PrimaryCareTableColumnKeys.UPDATE,
        showBorder: true,
      },
    };
  });
};

//   data: AestraPrimaryCareTable[],
// ) => {
//   return data.map((item) => {
//     const patientName: TableCellMultiline = {
//       mainText: capitalizeString(
//         item.patient_first_name + " " + item.patient_last_name,
//       ),
//       secondaryText: `MRN : ${item.patient_mrn}`,
//     };
//     return {
//       identifier: item.identifier,
//       patient_mrn: item.patient_mrn,
//       [PharmaPrimaryCareTableColumnKeys.DATE_OF_SERVICE]: moment(
//         item.created_at,
//       ).format("MM/DD/YYYY"),
//       [PharmaPrimaryCareTableColumnKeys.CASE_NAME]: item.case_name,
//       [PharmaPrimaryCareTableColumnKeys.PATIENT_NAME]: patientName,
//       [PharmaPrimaryCareTableColumnKeys.DATE_OF_BIRTH]: item.patient_dob,
//       [PharmaPrimaryCareTableColumnKeys.MEDICATION]: item.drug_name_onco_emr,
//       [PharmaPrimaryCareTableColumnKeys.PROVIDER_NAME]: (
//         (item.provider_first_name ?? "") +
//         " " +
//         (item.provider_last_name ?? "")
//       ).trim(),
//       [PharmaPrimaryCareTableColumnKeys.CMM_KEY]: {
//         data: item.cmm_result_key,
//       }
//       [PharmaPrimaryCareTableColumnKeys.QA_STATUS]: {
//       text: item.status || "No Status",
//         color:
//       BadgeTextColor[(item.status as OrderStatus) ?? OrderStatus.NO_STATUS],
//         bgColor:
//       BadgeColor[(item.status as OrderStatus) ?? OrderStatus.NO_STATUS],
//       },
//     [PharmaPrimaryCareTableColumnKeys.ACTION]: {
//       value: "retry-qa",
//         title: "Retry QA",
//           isDisabled: false,
//       },
//     [PharmaPrimaryCareTableColumnKeys.CASE_STATUS]: {
//       text: item.case_status,
//         color: getTextColorForStpStatus(item.case_status as CmmStpStatus),
//           bgColor: getBgColorForStpStatus(item.case_status as CmmStpStatus),
//       },
//     [PharmaPrimaryCareTableColumnKeys.CMM]: "View",
//       [PharmaPrimaryCareTableColumnKeys.CLINICAL]: "Note",
//     };
// });
// };
